import React, { useState, useCallback, useRef } from 'react';
import { useEffect } from 'react';
import HeatMapHelper from './HeatMapHelper';
import HeatMapItem from './HeatMapItem';
import './Heatmap.css';
import { ReactComponent as ArrowRight } from '../../assets/icons/arrow-right-s-line.svg';

//DataItem is the data item that is passed to the heatmap
interface DataItem {
  name?: string; //ticker
  label?: string; //Display name
  categoryid?: number; //sector id
  category?: string; //sector name
  subcategoryid?: number; //industry id
  subcategory?: string; //industry name
  value?: number | string; //value  e.g volume , market cap
  value1?: number | string; //value  e.g volume , market cap
  value2?: number | string; //value  e.g volume , market cap
  subitem?: any; //subitem
  ls?: number; //last price e.g 100
  nc?: number; //net change e.g 10
  pc?: number; //percentage  e.g 10%
}

//ProcessedDataItem is the data item that is passed to the renderer
interface ProcessedDataItem {
  name?: string;
  label?: string;
  categoryid?: number;
  category?: string;
  subcategoryid?: number;
  subcategory?: string;
  value?: number | string;
  value1?: number | string;
  value2?: number | string;
  left: string;
  top: string;
  width: string;
  height: string;
  subitem?: any;
  ls?: number;
  nc?: number;
  pc?: number;
}

export interface Props {
  page?: string;
  updateTime?: number;
  data: DataItem[];
  isGrouped?: boolean;
  colorBy?: string;
  heatmapColor?: {
    fontColor: string;
    color: string;
    min: number;
    max: number;
    label: string;
  }[];
  showSubitem?: boolean;
  chgHeatMapData?: (data: any[]) => void;
  resetHeatmapData?: () => void;
  tooltipTemplate?: string;
  tooltipConfig?: {
    showTicker?: boolean;
    showCompany?: boolean;
    showIndustry?: boolean;
    showLastPrice?: boolean;
    showNetChange?: boolean;
    showPercentageChange?: boolean;
    showVolume?: boolean;
    showMarketCap?: boolean;
    customFields?: { [key: string]: string };
  };
  tooltipCallback?: (tooltip: any) => void;
  onClickCallback?: (item: any) => void;
  showLabel?: boolean;
}

const Heatmap = ({
  page = '',
  updateTime,
  data,
  isGrouped,
  colorBy = 'pc',
  heatmapColor,
  showSubitem,
  chgHeatMapData,
  resetHeatmapData,
  tooltipTemplate,
  tooltipConfig,
  tooltipCallback,
  onClickCallback,
  showLabel = false,
}: Props) => {
  // Default tooltip configuration
  const defaultTooltipConfig = {
    showTicker: true,
    showCompany: true,
    showIndustry: true,
    showLastPrice: true,
    showNetChange: true,
    showPercentageChange: true,
    showVolume: true,
    showMarketCap: true,
    customFields: {},
  };

  // Merge user config with defaults
  const config = { ...defaultTooltipConfig, ...tooltipConfig };

  const _MINWDTH = 100; // cell minimun width
  const _MINHGHT = 100; // cell minimun height
  const _TITLEHGHT = 35; // height reserved for the group title bar

  const [processedData, setProcessedData] = useState<ProcessedDataItem[]>([]);
  const [tooltip, setTooltip] = useState<{
    ric: string;
    show: boolean;
    x: number;
    y: number;
    content: string;
  }>({
    ric: '',
    show: false,
    x: 0,
    y: 0,
    content: '',
  });

  const [animate, setAnimate] = useState<boolean>(false);
  const heatmapRef = useRef<HTMLDivElement>(null);
  const paneRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (data && data.length > 0) {
      //measure our own pane. `page` is not unique across widgets (several of
      //them pass "mini"), so getElementById would return another instance.
      const width = paneRef.current?.clientWidth || 0;
      const height = paneRef.current?.clientHeight || 0;
      //the pane is not measurable yet, keep the previous layout rather than
      //laying out against a 0x0 box, which makes squarify emit NaN positions
      if (width <= 0 || height <= 0) {
        return;
      }
      setProcessedData(processHeatmapData(data, width, height));

      //find data by tooltip.ric
      const item = data.find((item: DataItem) => item.name === tooltip.ric);
      if (item) {
        //update tooltip content if tooltip is showing
        setTooltip({ ...tooltip });
      }
    }
  }, [data, updateTime]);

  useEffect(() => {
    //let item = processedData.find((item: DataItem) => item.name === tooltip.ric);
    //const tooltipTemplate = processTooltipTemplate(item);
    if (tooltipCallback) {
      tooltipCallback(tooltip);
    }
  }, [tooltip]);

  const processHeatmapData = (
    rawData: DataItem[],
    width: number,
    height: number,
    marginX: number = 0,
    marginY: number = 0,
  ): any => {
    //console.log('processHeatmapData','rawData',rawData);
    try {
      //nothing can be laid out inside a zero or negative box: squarify would
      //return NaN / negative offsets and the cells would leak over their siblings
      if (!rawData || rawData.length === 0) {
        return [];
      }
      const paneWidth = width - marginX;
      const paneHeight = height - marginY;
      if (
        !isFinite(paneWidth) ||
        !isFinite(paneHeight) ||
        paneWidth <= 0 ||
        paneHeight <= 0
      ) {
        return [];
      }

      // Convert raw data to HeatMapItem objects
      //find min value on rawData
      const minValue = Math.min(
        ...rawData.map((item: DataItem) => Number(item.value || 0)),
      );
      const heatmapItems = rawData.map((item: DataItem) => {
        //console.log('item', item.name, item.value);
        const heatmapItem = new HeatMapItem();
        // Use value as the value for sizing, or fallback to a default value
        let rawvalue = Number(item.value) || 0;
        if (
          Number(item.value) === Number(item.pc) ||
          Number(item.value) === Number(item.nc)
        ) {
          rawvalue = Math.abs(rawvalue);
        }
        const value = Number(rawvalue) || 0;
        heatmapItem.setValue(value);
        heatmapItem.setName(item.name || '-');
        heatmapItem.setLabel(item.label || '-');
        heatmapItem.setCategory(item.category || '-');
        heatmapItem.setSubcategory(item.subcategory || '-');
        heatmapItem.setSubitem(item.subitem || undefined);
        heatmapItem.setPc(item.pc || 0);
        heatmapItem.setCategoryid(item.categoryid || 0);
        heatmapItem.setSubcategoryid(item.subcategoryid || 0);
        heatmapItem.setLs(item.ls || 0);
        heatmapItem.setNc(item.nc || 0);
        heatmapItem.setValue1(Number(item.value1) || 0);
        heatmapItem.setValue2(Number(item.value2) || 0);
        return heatmapItem;
      });

      // Use HeatMapHelper to generate layout
      const processedItems = HeatMapHelper.squarify(
        heatmapItems,
        marginX,
        width - marginX,
        marginY,
        height - marginY,
      );

      // Convert processed items to the format expected by the renderer
      const formattedData: ProcessedDataItem[] = processedItems.map((item) => ({
        name: item.getName(),
        value: item.getValue(), // Get the actual performance value
        left: item.getLeft() + 'px',
        top: item.getTop() + 'px',
        width: item.getWidth() + 'px',
        height: item.getHeight() + 'px',
        label: item.getLabel(),
        category: item.getCategory(),
        subcategory: item.getSubcategory(),
        subitem: item.getSubitem(),
        nc: item.getNc(),
        pc: item.getPc(),
        ls: item.getLs(),
        value1: item.getValue1(),
        value2: item.getValue2(),
      }));
      return formattedData;
    } catch (error) {
      console.error('Error processing heatmap data:', error);
      return [];
    }
  };

  const renderText = (
    forceHidden: boolean,
    item: ProcessedDataItem,
  ): React.JSX.Element => {
    if (forceHidden) {
      return <div></div>;
    }

    var hmcwdth = Number(item.width.replace('px', ''));
    var hmchght = Number(item.height.replace('px', ''));

    const cellWidthPx = hmcwdth;
    const cellHeightPx = hmchght;
    //console.log('cellWidthPx', cellWidthPx, 'cellHeightPx', cellHeightPx);
    // Calculate font size based on minimum dimension with more responsive scaling
    var minDimensionPx = Math.min(cellWidthPx, cellHeightPx);
    // More aggressive scaling - smaller fonts when resized
    let fontSize = Math.max(8, Math.min(20, minDimensionPx * 0.12)); // Reduced from 0.15 to 0.12
    let lineHeight = Math.max(8, Math.min(20, minDimensionPx * 0.12));
    let level = 0;

    // Adjust font size for very small cells with smaller thresholds
    if (minDimensionPx < 20) {
      fontSize = 0;
      lineHeight = 0;
      level = 1;
    } else if (minDimensionPx < 40) {
      fontSize = 8;
      lineHeight = 8;
      level = 2;
    } else if (minDimensionPx < 60) {
      fontSize = 10;
      lineHeight = 10;
      level = 3;
    } else if (minDimensionPx < 80) {
      fontSize = 12;
      lineHeight = 12;
      level = 3;
    } else if (minDimensionPx < 100) {
      fontSize = 14;
      lineHeight = 14;
      level = 3;
    } else {
      fontSize = 18; // Reduced from 20 to 18
      lineHeight = 18;
      level = 4;
    }

    //calculate the item.ticker width based on the item width and font size
    if (item.name) {
      const tickerWidth = item.name.length * fontSize + 10;
      if (tickerWidth > Number(item.width.replace('px', ''))) {
        fontSize = 0;
        lineHeight = 0;
        level = 1;
      }
    }
    //if ((minDimensionPx >= 80 && item.label)) {
    //    const labelWidth = item.label.length * (fontSize - 3) + 10;
    //    if (labelWidth > Number(item.width.replace("px", ""))) {
    //        minDimensionPx = 60
    //    }
    //}
    //console.log(item.name, 'minDimensionPx', minDimensionPx.toFixed(0), 'fontSize', fontSize);
    const fontColor = getHeatmapFontColor(
      item[colorBy as keyof ProcessedDataItem],
    );
    //the feed sends "-" for fields it has no value for (e.g. a closed market),
    //so only format when the metric is actually a number
    const rawValue = Number(item[colorBy as keyof ProcessedDataItem]);
    let value = '-';
    if (isFinite(rawValue)) {
      value = rawValue.toFixed(2);
      if (colorBy === 'pc') {
        //compare the rounded value, as before: something that displays as
        //"0.00" must not pick up a "+" sign
        if (Number(value) > 0) {
          value = '+' + value;
        }
        value = value + '%';
      }
    }
    if (showLabel) {
      if (minDimensionPx <= 80) {
        return <div></div>;
      }
      fontSize = 11;
      lineHeight = 11;
      return (
        <div className="heatmapboxlabel">
          <div
            className="heatmapboxtext"
            style={{
              fontWeight: 'bold',
              fontSize: `${fontSize}px`,
              lineHeight: `${lineHeight}px`,
              color: fontColor,
              whiteSpace: 'wrap',
            }}
          >
            {item.name}
          </div>
          <div
            className="heatmapboxtext"
            style={{
              fontSize: `${fontSize - 2}px`,
              lineHeight: `${lineHeight - 2}px`,
              color: fontColor,
            }}
          >
            {value}
          </div>
        </div>
      );
    }

    return fontSize > 0 ? (
      <div className="heatmapboxlabel">
        <div
          className="heatmapboxtext"
          style={{
            fontWeight: 'bold',
            fontSize: `${fontSize}px`,
            lineHeight: `${lineHeight}px`,
            color: fontColor,
          }}
        >
          {item.name}
        </div>
        {minDimensionPx >= 80 && item.label && (
          <div
            className="heatmapboxtext"
            style={{
              fontSize: `${fontSize - 3}px`,
              lineHeight: `${lineHeight - 3}px`,
              color: fontColor,
            }}
          >
            {item.label}
          </div>
        )}
        {minDimensionPx > 50 && (
          <div
            className="heatmapboxtext"
            style={{
              fontSize: `${fontSize - 2}px`,
              lineHeight: `${lineHeight - 2}px`,
              color: fontColor,
            }}
          >
            {value}
          </div>
        )}
      </div>
    ) : (
      <div></div>
    );
  };
  const handleRightClick = (e: React.MouseEvent, item: ProcessedDataItem) => {
    // console.log('handleRightClick', item);
    e.preventDefault();

    if (
      !item.subitem ||
      item.subitem.length == 0 ||
      item.name === item.subcategory
    ) {
      if (heatmapRef.current) {
        let x =
          Number(item.left.replace('%', '')) +
          Number(item.width.replace('%', '')) / 2;
        let y =
          Number(item.top.replace('%', '')) +
          Number(item.height.replace('%', '')) / 2;
        heatmapRef.current.style.setProperty(
          '--heatmapFadeIn-start-offset',
          `${x}%`,
        );
        heatmapRef.current.style.setProperty(
          '--heatmapFadeIn-end-offset',
          `${y}%`,
        );
      }
      setAnimate(true);
      resetHeatmapData && resetHeatmapData();
    }
  };
  const handleClick = (item: ProcessedDataItem) => {
    // console.log('handleClick item', item);
    if (item.subitem && item.subitem.length > 0) {
      if (heatmapRef.current) {
        let x =
          Number(item.left.replace('%', '')) +
          Number(item.width.replace('%', '')) / 2;
        let y =
          Number(item.top.replace('%', '')) +
          Number(item.height.replace('%', '')) / 2;
        heatmapRef.current.style.setProperty(
          '--heatmapFadeIn-start-offset',
          `${x}%`,
        );
        heatmapRef.current.style.setProperty(
          '--heatmapFadeIn-end-offset',
          `${y}%`,
        );
      }

      setAnimate(true);
      //clear tooltip
      setTooltip({
        ric: item.name || '',
        show: true,
        x: 0,
        y: 0,
        content: '',
      });
      chgHeatMapData && chgHeatMapData(item.subitem);
    } else {
      //setProcessedData([]);
    }
  };

  const constrainTooltipPosition = (
    item: ProcessedDataItem,
    e: React.MouseEvent,
  ) => {
    // Estimate tooltip dimensions (these are approximate values from CSS)
    // Calculate initial position based on item position and mouse offset
    let x = e.pageX;
    let y = e.pageY;
    return { x, y };
  };

  //available area left for the nested heatmap once the title bar is taken out.
  //small groups can be shorter than the title bar itself, so clamp at 0.
  const getSubitemArea = (item: ProcessedDataItem) => {
    const width = Number(item.width.replace('px', ''));
    const height = Number(item.height.replace('px', '')) - _TITLEHGHT;
    return {
      width: isFinite(width) ? Math.max(0, width) : 0,
      height: isFinite(height) ? Math.max(0, height) : 0,
    };
  };

  const renderSubitem = (item: ProcessedDataItem): React.JSX.Element | null => {
    if (!item.subitem || !showSubitem) {
      return null;
    }
    const area = getSubitemArea(item);
    if (area.width <= 0 || area.height <= 0) {
      return null;
    }
    return renderHeatMap2(
      item,
      processHeatmapData(item.subitem, area.width, area.height),
    );
  };

  const addCells = (
    celldatalist: ProcessedDataItem[],
    forceHidden: boolean = false,
  ): React.JSX.Element[] => {
    //console.log('celldatalist',celldatalist);

    return celldatalist.map((item: ProcessedDataItem, index: number) => (
      <div
        onClick={() => {
          onClickCallback && onClickCallback(item);
        }}
        className={`heatmapbox ${showSubitem && item.subitem && item.subitem.length > 0 ? 'heatmapboxtopborder' : ''}`}
        style={{
          cursor: 'pointer',
          left: item.left,
          width: item.width,
          top: item.top,
          height: item.height,
          backgroundColor:
            isGrouped && item.subitem && item.subitem.length > 0
              ? '#F8F8F8'
              : getHeatmapColor(item[colorBy as keyof ProcessedDataItem]),
          pointerEvents:
            isGrouped && item.subitem && item.subitem.length > 0
              ? 'auto'
              : 'auto',
        }}
        onContextMenu={(e) => handleRightClick(e, item)}
        key={index}
        onMouseEnter={(e) => {
          const constrainedPosition = constrainTooltipPosition(item, e);
          setTooltip({
            ric: item.name || '',
            show: true,
            x: constrainedPosition.x,
            y: constrainedPosition.y,
            content: '',
          });
        }}
        onMouseMove={(e) => {
          //if no move, do not update tooltip
          if (e.movementX === 0 && e.movementY === 0) {
            return;
          }
          const constrainedPosition = constrainTooltipPosition(item, e);
          setTooltip((prev) => ({
            ...prev,
            x: constrainedPosition.x,
            y: constrainedPosition.y,
          }));
        }}
        onMouseLeave={() => {
          setTooltip((prev) => ({ ...prev, show: false }));
        }}
        onMouseOut={() => {
          setTooltip((prev) => ({ ...prev, show: false }));
        }}
      >
        {/*{console.log('renderText', item.width, item.height, item.name, item.label, item.pc)}*/}
        {(!item.subitem || item.subitem.length == 0) &&
          renderText(forceHidden, item)}
        {renderSubitem(item)}
      </div>
    ));
  };
  const addGroupCells = (
    celldatalist: ProcessedDataItem[],
    forceHidden: boolean = false,
  ): React.JSX.Element[] => {
    //console.log('celldatalist',celldatalist);
    return celldatalist.map((item: ProcessedDataItem, index: number) => (
      <div
        className={`heatmapbox ${showSubitem && item.subitem && item.subitem.length > 0 ? 'heatmapboxtopborder' : ''}`}
        style={{
          cursor: 'pointer',
          left: item.left,
          width: item.width,
          top: item.top,
          height: item.height,
          backgroundColor:
            isGrouped && item.subitem && item.subitem.length > 0
              ? '#F8F8F8'
              : getHeatmapColor(item[colorBy as keyof ProcessedDataItem]),
          pointerEvents:
            isGrouped && item.subitem && item.subitem.length > 0
              ? 'auto'
              : 'auto',
        }}
        onContextMenu={(e) => handleRightClick(e, item)}
        key={index}
      >
        {renderSubitem(item)}
      </div>
    ));
  };
  const getHeatmapColor = (value: number | undefined): string => {
    const nval = Number(String(value).replace(',', ''));
    //console.log('getHeatmapColor', nval);

    if (heatmapColor) {
      //console.log('nval:', nval, 'type:', typeof nval);
      const matchingColor = heatmapColor.find((color) => {
        const isInRange = nval >= color.min && nval <= color.max;
        //console.log(`Checking range ${nval}: ${color.min} <= ${nval} <= ${color.max}, result: ${isInRange}`);
        return isInRange;
      });
      //console.log('matchingColor:', matchingColor);
      return matchingColor?.color || '#F8F8F8';
    } else {
      return '#F8F8F8';
    }
  };
  const getHeatmapFontColor = (value: string | number | undefined): string => {
    const nval = Number(String(value).replace(',', ''));
    if (heatmapColor) {
      //console.log('nval:', nval, 'type:', typeof nval);
      const matchingColor = heatmapColor.find((color) => {
        const isInRange = nval >= color.min && nval <= color.max;
        //console.log(`Checking range: ${color.min} <= ${nval} <= ${color.max}, result: ${isInRange}`);
        return isInRange;
      });
      //console.log('matchingColor:', matchingColor);
      //no bucket matched (the feed sends "-" for missing metrics). The fallback
      //cell colour is near-white, so white text there would be unreadable.
      return matchingColor?.fontColor || 'var(--text-primary)';
    } else {
      return 'var(--text-primary)';
    }
  };

  const handleAnimationEnd = () => {
    // console.log('Heatmap animation ended');
    setAnimate(false);
  };

  const renderHeatMap = (data: ProcessedDataItem[]): React.JSX.Element => {
    return data.length > 0 ? (
      <div
        ref={heatmapRef}
        className={`heatmapboxcontentpane ${animate ? 'heatmap-animate' : ''}`}
        onAnimationEnd={handleAnimationEnd}
      >
        {isGrouped && data[0].subitem && data[0].subitem.length > 0
          ? addGroupCells(data)
          : addCells(data)}
      </div>
    ) : (
      <div></div>
    );
  };
  const renderHeatMap2 = (
    parentItem: ProcessedDataItem,
    data: ProcessedDataItem[],
  ): React.JSX.Element => {
    return data.length > 0 ? (
      <div className="heatmapboxcontentpane2">
        <div
          className="heatmapboxtitle"
          onClick={() => {
            handleClick(parentItem);
          }}
        >
          <div className="heatmapboxtitletext">
            {data[0].category} <ArrowRight />
          </div>
        </div>
        <div className="heatmapboxcontentpane2">{addCells(data)}</div>
      </div>
    ) : (
      <div></div>
    );
  };

  return (
    <div
      key={page}
      ref={paneRef}
      className="heatmapboxcontentpane"
      id={page}
      style={{ height: '100%' }}
    >
      {renderHeatMap(processedData)}
    </div>
  );
};

export default Heatmap;
